import type { Resource } from '../lib/mockData';
import { useEffect } from 'react';

interface ReaderModalProps {
  resource: Resource | null;
  onClose: () => void;
}

export function ReaderModal({ resource, onClose }: ReaderModalProps) {
  useEffect(() => {
    if (resource) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [resource]);

  if (!resource) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{resource.type === 'textbook' ? '📖' : '✍️'}</span>
            <div>
              <h2 className="text-xl font-bold">{resource.title}</h2>
              <p className="text-sm text-blue-100 dark:text-gray-300">{resource.subject} - {resource.type}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-blue-800 dark:hover:bg-blue-900 rounded-full p-2 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 prose prose-sm md:prose max-w-none">
            {resource.content.split('\n').map((line, index) => {
              // Handle headings
              if (line.startsWith('# ')) {
                return <h1 key={index} className="text-3xl font-bold mt-6 mb-4 text-gray-900 dark:text-white">{line.substring(2)}</h1>;
              }
              if (line.startsWith('## ')) {
                return <h2 key={index} className="text-2xl font-bold mt-5 mb-3 text-gray-800 dark:text-gray-200">{line.substring(3)}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={index} className="text-xl font-semibold mt-4 mb-2 text-gray-700 dark:text-gray-300">{line.substring(4)}</h3>;
              }
              
              // Handle bold text
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={index} className="font-bold text-gray-800 dark:text-gray-200 mt-3 mb-2">{line.replace(/\*\*/g, '')}</p>;
              }
              
              // Handle list items
              if (line.startsWith('- ')) {
                return <li key={index} className="ml-6 text-gray-700 dark:text-gray-300 list-disc">{line.substring(2)}</li>;
              }
              if (/^\d+\.\s/.test(line)) {
                return <li key={index} className="ml-6 text-gray-700 dark:text-gray-300 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
              }
              
              // Handle empty lines
              if (line.trim() === '') {
                return <div key={index} className="h-2"></div>;
              }
              
              // Regular paragraphs
              return <p key={index} className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">{line}</p>;
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-100 dark:bg-gray-900 px-6 py-4 rounded-b-lg flex items-center justify-between border-t dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Uploaded:</span> {new Date(resource.uploadedAt).toLocaleDateString()}
          </div>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
